// color-exempt-file — getThemeColors() est une palette de marque par type de
// contenu (form/availability/quizz/date), volontairement littérale comme
// doo-blue/doo-green/doo-purple, avec paire light/dark sur chaque valeur
// texte/fond-teinté depuis Phase 6 ui-themes (bordures/remplissages pleins
// déjà mi-saturation, lisibles sur les deux fonds, inchangés).
import {
  DashboardPoll,
  ConversationItem,
  FilterType,
  ContentTypeFilter,
} from "./types";
import { getConversations } from "@core/lib/storage/ConversationStorageSimple";
import i18n from "@core/i18n";

// Trouver la conversation liée à un sondage
// userId optionnel pour filtrer par utilisateur (sécurité)
export function findRelatedConversation(
  poll: DashboardPoll,
  userId?: string | null,
): string | undefined {
  if (poll.relatedConversationId) return poll.relatedConversationId;

  try {
    const allConversations = getConversations();
    // Filtrer par utilisateur si userId est fourni
    const conversations = userId
      ? allConversations.filter((conv) => conv.userId === userId)
      : allConversations;

    const match = conversations.find((conv) => {
      const metadata = conv.metadata || {};
      return metadata?.pollGenerated && metadata?.pollId === poll.id;
    });
    return match?.id;
  } catch {
    return undefined;
  }
}

// Couleur du badge de statut
export function getStatusColor(status: DashboardPoll["status"]): string {
  switch (status) {
    case "draft":
      return "bg-muted text-muted-foreground";
    case "active":
      return "bg-success text-success-foreground";
    case "closed":
      return "bg-primary/10 text-primary";
    case "archived":
      return "bg-destructive/10 text-destructive";
    default:
      return "bg-muted text-muted-foreground";
  }
}

// Label du statut
export function getStatusLabel(status: DashboardPoll["status"]): string {
  switch (status) {
    case "draft":
      return i18n.t("polls:dashboard_status_draft");
    case "active":
      return i18n.t("polls:dashboard_status_active");
    case "closed":
      return i18n.t("polls:dashboard_status_closed");
    case "archived":
      return i18n.t("polls:dashboard_status_archived");
    default:
      return status;
  }
}

// Filtrer les items selon le filtre, la recherche, les tags et les dossiers
export function filterConversationItems(
  items: ConversationItem[],
  filter: FilterType,
  searchQuery: string,
  selectedTags?: string[],
  selectedFolderId?: string | null,
  contentTypeFilter?: ContentTypeFilter,
): ConversationItem[] {
  return items.filter((item) => {
    // Filtre par statut (appliqué au poll si existe)
    // Pour "all", on affiche tout
    // Pour les autres filtres (draft, active, closed, archived), on n'affiche que les items avec un poll ayant le statut correspondant
    const matchesFilter =
      filter === "all" || (item.poll && item.poll.status === filter);

    // Filtre par type de contenu
    const matchesContentType =
      !contentTypeFilter ||
      contentTypeFilter === "all" ||
      (contentTypeFilter === "conversations" && !item.poll) ||
      (contentTypeFilter === "date" &&
        item.poll &&
        item.poll?.type === "date") ||
      (contentTypeFilter === "availability" &&
        (!item.poll || item.poll?.type === "availability")) ||
      (contentTypeFilter === "form" &&
        (!item.poll || item.poll?.type === "form")) ||
      (contentTypeFilter === "quizz" &&
        (!item.poll || item.poll?.type === "quizz"));

    // Recherche dans le titre de la conversation et du poll
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      item.conversationTitle.toLowerCase().includes(searchLower) ||
      (item.poll?.title?.toLowerCase().includes(searchLower) ?? false) ||
      (item.poll?.description?.toLowerCase().includes(searchLower) ?? false) ||
      (item.tags?.some((tag) => tag.toLowerCase().includes(searchLower)) ??
        false);

    // Filtre par tags
    const matchesTags =
      !selectedTags ||
      selectedTags.length === 0 ||
      selectedTags.every((tag) => item.tags?.includes(tag));

    // Filtre par dossier
    // Si selectedFolderId === null, on filtre uniquement les items sans dossier
    // Si selectedFolderId === undefined, on affiche tous les items
    // Sinon, on filtre par folderId spécifique
    const matchesFolder =
      selectedFolderId === undefined ||
      (selectedFolderId === null
        ? !item.folderId
        : item.folderId === selectedFolderId);

    return (
      matchesFilter &&
      matchesContentType &&
      matchesSearch &&
      matchesTags &&
      matchesFolder
    );
  });
}

// Couleurs d'accent par type de contenu dans le dashboard partagé
// (form=violet, availability=green, quizz=amber, date=blue). Historiquement
// codées en dur pour le sombre seul (text-X-400 etc, illisible sur fond
// clair sous Corail/Périwinkle) ; les valeurs de texte/fond-teinté
// reçoivent désormais une paire light/dark, les bordures/remplissages
// pleins (déjà mi-saturation, lisibles sur les deux fonds) restent
// inchangés.
export function getThemeColors(type: ContentTypeFilter) {
  switch (type) {
    case "form":
      return {
        bg: "bg-violet-100 dark:bg-violet-900/20",
        border: "border-violet-500/50",
        text: "text-violet-700 dark:text-violet-400",
        progressBg: "bg-violet-500",
        hoverText:
          "group-hover:text-violet-600 dark:group-hover:text-violet-300",
        buttonText:
          "text-violet-700 dark:text-violet-400 hover:text-violet-600 dark:hover:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/20",
        primaryButton: "bg-violet-600 hover:bg-violet-700",
        checkbox: "bg-violet-600 border-violet-600",
        lightBadge: "bg-violet-500/20 text-violet-700 dark:text-violet-400",
        selectionBg: "bg-violet-100 dark:bg-violet-900/20",
        activeBorder: "border-violet-500",
        ring: "ring-violet-500/50",
        linkText:
          "text-violet-700 dark:text-violet-400 hover:text-violet-600 dark:hover:text-violet-300",
      };
    case "availability":
      return {
        bg: "bg-green-100 dark:bg-green-900/20",
        border: "border-green-500/50",
        text: "text-green-700 dark:text-green-400",
        progressBg: "bg-green-500",
        hoverText: "group-hover:text-green-600 dark:group-hover:text-green-300",
        buttonText:
          "text-green-700 dark:text-green-400 hover:text-green-600 dark:hover:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/20",
        primaryButton: "bg-green-600 hover:bg-green-700",
        checkbox: "bg-green-600 border-green-600",
        lightBadge: "bg-green-500/20 text-green-700 dark:text-green-400",
        selectionBg: "bg-green-100 dark:bg-green-900/20",
        activeBorder: "border-green-500",
        ring: "ring-green-500/50",
        linkText:
          "text-green-700 dark:text-green-400 hover:text-green-600 dark:hover:text-green-300",
      };
    case "quizz":
      return {
        bg: "bg-amber-100 dark:bg-amber-900/20",
        border: "border-amber-500/50",
        text: "text-amber-700 dark:text-amber-400",
        progressBg: "bg-amber-500",
        hoverText: "group-hover:text-amber-600 dark:group-hover:text-amber-300",
        buttonText:
          "text-amber-700 dark:text-amber-400 hover:text-amber-600 dark:hover:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/20",
        primaryButton: "bg-amber-600 hover:bg-amber-700",
        checkbox: "bg-amber-600 border-amber-600",
        lightBadge: "bg-amber-500/20 text-amber-700 dark:text-amber-400",
        selectionBg: "bg-amber-100 dark:bg-amber-900/20",
        activeBorder: "border-amber-500",
        ring: "ring-amber-500/50",
        linkText:
          "text-amber-700 dark:text-amber-400 hover:text-amber-600 dark:hover:text-amber-300",
      };
    default: // date, all, conversations
      return {
        bg: "bg-blue-100 dark:bg-blue-900/20",
        border: "border-blue-500/50",
        text: "text-blue-700 dark:text-blue-400",
        progressBg: "bg-blue-500",
        hoverText: "group-hover:text-blue-600 dark:group-hover:text-blue-300",
        buttonText:
          "text-blue-700 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/20",
        primaryButton: "bg-blue-600 hover:bg-blue-700",
        // Le type par defaut (date/all/conversations) suit le theme graphique
        // actif (Corail/Periwinkle) : token primary, pas un bleu code en dur.
        checkbox: "bg-primary border-primary",
        lightBadge: "bg-blue-500/20 text-blue-700 dark:text-blue-400",
        selectionBg: "bg-blue-100 dark:bg-blue-900/20",
        activeBorder: "border-blue-500",
        ring: "ring-blue-500/50",
        linkText:
          "text-blue-700 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300",
      };
  }
}
